import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { authFetch } from "../utils/auth";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type StatusTone = "success" | "warning" | "danger";

type MetricCard = {
  label: string;
  value: string;
  delta: string;
  tone: StatusTone;
};

type ModelUsage = {
  name: string;
  value: number;
  color: string;
};

type DailyUsage = {
  day: string;
  tokens: number;
  cost: number;
};

type InfrastructureNode = {
  name: string;
  region: string;
  uptime: string;
  latency: string;
  status: "online" | "degraded" | "offline";
};

type IssuesSummaryResponse = {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
};

const metricHead: MetricCard[] = [
  {
    label: "이번 달 토큰",
    value: "18.42M",
    delta: "+12.8% MoM",
    tone: "success",
  },
  {
    label: "예상 청구액",
    value: "₩1,284,000",
    delta: "예산의 71%",
    tone: "warning",
  },
];

const metricTail: MetricCard[] = [
  { label: "SLA 가동률", value: "99.94%", delta: "최근 30일", tone: "success" },
];

/** 완료(done)가 아닌 이슈 = 오픈 이슈 */
const countOpenIssues = (byStatus: Record<string, number>) => {
  const done = byStatus.done ?? 0;
  const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
  return Math.max(0, total - done);
};

const modelUsage: ModelUsage[] = [
  { name: "GPT-4.1", value: 42, color: "#2563eb" },
  { name: "Claude 3.7", value: 28, color: "#7c3aed" },
  { name: "Embedding", value: 18, color: "#06b6d4" },
  { name: "Vision", value: 12, color: "#f59e0b" },
];

const dailyUsage: DailyUsage[] = [
  { day: "Mon", tokens: 1.8, cost: 88 },
  { day: "Tue", tokens: 2.4, cost: 116 },
  { day: "Wed", tokens: 2.1, cost: 104 },
  { day: "Thu", tokens: 3.2, cost: 158 },
  { day: "Fri", tokens: 2.8, cost: 139 },
  { day: "Sat", tokens: 3.7, cost: 184 },
  { day: "Sun", tokens: 2.4, cost: 119 },
];

const budgetGauge = [{ name: "Used", value: 71, fill: "#2563eb" }];

const infrastructureNodes: InfrastructureNode[] = [
  {
    name: "API Gateway",
    region: "ap-northeast-2",
    uptime: "99.99%",
    latency: "42ms",
    status: "online",
  },
  {
    name: "Inference Worker",
    region: "ap-northeast-2",
    uptime: "99.91%",
    latency: "87ms",
    status: "online",
  },
  {
    name: "Vector Database",
    region: "ap-northeast-2",
    uptime: "99.84%",
    latency: "64ms",
    status: "degraded",
  },
  {
    name: "Webhook Bridge",
    region: "global edge",
    uptime: "100%",
    latency: "28ms",
    status: "online",
  },
];

const recentEvents = [
  "Claude 3.7 usage cap raised to 4.2M tokens",
  "Vector DB heartbeat delayed in Seoul region",
  "3 urgent issues need PM confirmation",
  "April invoice draft is ready for review",
];

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 3vw, 30px);
`;

const PageHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: end;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.05;
  letter-spacing: -0.045em;
`;

const Description = styled.p`
  max-width: 760px;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.75;
`;

const HeaderPanel = styled.div`
  min-width: 260px;
  padding: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};

  @media (max-width: 767px) {
    min-width: 0;
  }
`;

const HeaderPanelLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  font-weight: 800;
`;

const HeaderPanelValue = styled.strong`
  display: block;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(24px, 3vw, 32px);
  letter-spacing: -0.04em;
`;

const HeaderPanelMeta = styled.p`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.success};
  font-size: 13px;
  font-weight: 800;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(14px, 2vw, 18px);

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const MetricPanel = styled.article`
  padding: clamp(18px, 2vw, 22px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
`;

const MetricLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  font-weight: 800;
`;

const MetricValue = styled.strong`
  display: block;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1;
  letter-spacing: -0.045em;
`;

const MetricDelta = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  margin-top: 14px;
  padding: 6px 10px;
  border-radius: 999px;
  color: ${({ theme, $tone }) => theme.colors[$tone]};
  background: ${({ $tone }) =>
    $tone === "success"
      ? "rgba(34, 197, 94, 0.12)"
      : $tone === "warning"
        ? "rgba(245, 158, 11, 0.14)"
        : "rgba(239, 68, 68, 0.12)"};
  font-size: 12px;
  font-weight: 900;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.65fr);
  gap: clamp(18px, 2vw, 24px);
  align-items: stretch;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  min-width: 0;
  padding: clamp(20px, 2.5vw, 28px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 22px;
`;

const CardTitleGroup = styled.div`
  min-width: 0;
`;

const CardTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(18px, 2vw, 22px);
  line-height: 1.2;
  letter-spacing: -0.03em;
`;

const CardDescription = styled.p`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  line-height: 1.6;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
`;

const ChartBox = styled.div`
  width: 100%;
  height: 320px;

  @media (max-width: 767px) {
    height: 260px;
  }
`;

const UsageSplit = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 20px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DonutBox = styled.div`
  width: 100%;
  height: 240px;
`;

const ModelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModelRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 800;
`;

const ModelDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const ModelTrack = styled.span`
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const ModelFill = styled.span<{ $color: string; $value: number }>`
  display: block;
  width: ${({ $value }) => `${$value}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ $color }) => $color};
`;

const SideStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(18px, 2vw, 24px);
`;

const GaugeBox = styled.div`
  position: relative;
  height: 220px;
`;

const GaugeCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 16px;
  pointer-events: none;

  strong {
    color: ${({ theme }) => theme.colors.text};
    font-size: clamp(34px, 4vw, 44px);
    line-height: 1;
    letter-spacing: -0.05em;
  }

  span {
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-weight: 900;
  }
`;

const BillingSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const BillingItem = styled.div`
  padding: 14px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.surfaceMuted};

  span {
    display: block;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    display: block;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 18px;
    letter-spacing: -0.03em;
  }
`;

const InfraGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfraNode = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const InfraHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const InfraName = styled.strong`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
`;

const Heartbeat = styled.span<{ $status: InfrastructureNode["status"] }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme, $status }) =>
    $status === "online"
      ? theme.colors.success
      : $status === "degraded"
        ? theme.colors.warning
        : theme.colors.danger};
  box-shadow: 0 0 0 6px
    ${({ $status }) =>
      $status === "online"
        ? "rgba(34, 197, 94, 0.14)"
        : $status === "degraded"
          ? "rgba(245, 158, 11, 0.16)"
          : "rgba(239, 68, 68, 0.14)"};
`;

const InfraMeta = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  span {
    display: block;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 11px;
    font-weight: 800;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 13px;
  }
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EventItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  line-height: 1.55;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    margin-top: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const TooltipBox = styled.div`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
  font-size: 12px;
  font-weight: 800;
`;

const DashboardPage: React.FC = () => {
  const [issueSummary, setIssueSummary] = useState<IssuesSummaryResponse | null>(null);
  const [issueSummaryLoading, setIssueSummaryLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      try {
        const res = await authFetch("/issues/summary/");
        if (!res.ok) throw new Error("summary failed");
        const data = (await res.json()) as IssuesSummaryResponse;
        if (!cancelled) setIssueSummary(data);
      } catch {
        if (!cancelled) setIssueSummary(null);
      } finally {
        if (!cancelled) setIssueSummaryLoading(false);
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo((): MetricCard[] => {
    let openCard: MetricCard;

    if (issueSummaryLoading) {
      openCard = {
        label: "오픈 이슈",
        value: "—",
        delta: "불러오는 중…",
        tone: "warning",
      };
    } else if (!issueSummary) {
      openCard = {
        label: "오픈 이슈",
        value: "—",
        delta: "API 연결 실패",
        tone: "warning",
      };
    } else {
      const open = countOpenIssues(issueSummary.byStatus);
      const critical = issueSummary.byPriority.critical ?? 0;
      let tone: StatusTone = "success";
      if (critical > 0) tone = "danger";
      else if (open > 0) tone = "warning";

      openCard = {
        label: "오픈 이슈",
        value: String(open),
        delta:
          critical > 0
            ? `긴급 ${critical}건`
            : open > 0
              ? `미완료 ${open}건`
              : "미완료 없음",
        tone,
      };
    }

    return [...metricHead, openCard, ...metricTail];
  }, [issueSummary, issueSummaryLoading]);

  return (
    <Page>
      <PageHeader>
        <HeaderCopy>
          <Eyebrow>Executive Overview</Eyebrow>
          <Title>Neural OS Dashboard (테스트 데이터 입니다.)</Title>
          <Description>
            고객사의 AI 사용량, 예상 청구액, 운영 이슈, 인프라 상태를 하나의
            관리자 콘솔에서 빠르게 확인할 수 있는 통합 요약 화면입니다.
          </Description>
        </HeaderCopy>
        <HeaderPanel>
          <HeaderPanelLabel>이번 달 예상 절감 효과</HeaderPanelLabel>
          <HeaderPanelValue>₩8.7M</HeaderPanelValue>
          <HeaderPanelMeta>자동화 워크플로우 기준 +18.4%</HeaderPanelMeta>
        </HeaderPanel>
      </PageHeader>

      <MetricGrid>
        {metrics.map((metric) => (
          <MetricPanel key={metric.label}>
            <MetricLabel>{metric.label}</MetricLabel>
            <MetricValue>{metric.value}</MetricValue>
            <MetricDelta $tone={metric.tone}>{metric.delta}</MetricDelta>
          </MetricPanel>
        ))}
      </MetricGrid>

      <DashboardGrid>
        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardTitle>일일 토큰 사용량</CardTitle>
              <CardDescription>
                최근 7일 기준 LLM 요청량과 비용 추이를 추적합니다.
              </CardDescription>
            </CardTitleGroup>
            <Pill>Live Usage</Pill>
          </CardHeader>
          <ChartBox>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={dailyUsage}
                margin={{ top: 12, right: 16, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id="tokenGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(148, 163, 184, 0.24)"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <Tooltip
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TooltipBox>
                        {label}: {payload[0].value}M tokens
                      </TooltipBox>
                    ) : null
                  }
                />
                <Area
                  type="monotone"
                  dataKey="tokens"
                  stroke="#2563eb"
                  strokeWidth={3}
                  fill="url(#tokenGradient)"
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartBox>
        </Card>

        <SideStack>
          <Card>
            <CardHeader>
              <CardTitleGroup>
                <CardTitle>예산 소진율</CardTitle>
                <CardDescription>
                  월간 AI 예산 대비 현재 사용량입니다.
                </CardDescription>
              </CardTitleGroup>
            </CardHeader>
            <GaugeBox>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  data={budgetGauge}
                  cx="50%"
                  cy="58%"
                  innerRadius="70%"
                  outerRadius="100%"
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={16}
                    background={{ fill: "rgba(148, 163, 184, 0.18)" }}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <GaugeCenter>
                <strong>71%</strong>
                <span>₩1.28M / ₩1.80M</span>
              </GaugeCenter>
            </GaugeBox>
            <BillingSummary>
              <BillingItem>
                <span>평균 단가</span>
                <strong>₩69.7 / 1K</strong>
              </BillingItem>
              <BillingItem>
                <span>예상 초과</span>
                <strong>없음</strong>
              </BillingItem>
            </BillingSummary>
          </Card>
        </SideStack>
      </DashboardGrid>

      <DashboardGrid>
        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardTitle>모델별 사용 비중</CardTitle>
              <CardDescription>
                프로덕션 워크로드에서 호출된 모델별 토큰 점유율입니다.
              </CardDescription>
            </CardTitleGroup>
          </CardHeader>
          <UsageSplit>
            <DonutBox>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={modelUsage}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={3}
                  >
                    {modelUsage.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload?.length ? (
                        <TooltipBox>
                          {payload[0].name}: {payload[0].value}%
                        </TooltipBox>
                      ) : null
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            </DonutBox>
            <ModelList>
              {modelUsage.map((model) => (
                <ModelRow key={model.name}>
                  <ModelDot $color={model.color} />
                  <span>{model.name}</span>
                  <strong>{model.value}%</strong>
                  <ModelTrack>
                    <ModelFill $color={model.color} $value={model.value} />
                  </ModelTrack>
                </ModelRow>
              ))}
            </ModelList>
          </UsageSplit>
        </Card>

        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardTitle>비용 발생 추이</CardTitle>
              <CardDescription>
                일 단위 예상 비용을 천 원 단위로 표시합니다.
              </CardDescription>
            </CardTitleGroup>
          </CardHeader>
          <ChartBox>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dailyUsage}
                margin={{ top: 12, right: 0, bottom: 0, left: -18 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(148, 163, 184, 0.24)"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <Tooltip
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TooltipBox>
                        {label}: ₩{payload[0].value}K
                      </TooltipBox>
                    ) : null
                  }
                />
                <Bar dataKey="cost" fill="#7c3aed" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartBox>
        </Card>
      </DashboardGrid>

      <DashboardGrid>
        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardTitle>인프라 상태</CardTitle>
              <CardDescription>
                핵심 노드의 가동률, 지연시간, 하트비트 상태를 확인합니다.
              </CardDescription>
            </CardTitleGroup>
            <Pill>4 Nodes</Pill>
          </CardHeader>
          <InfraGrid>
            {infrastructureNodes.map((node) => (
              <InfraNode key={node.name}>
                <InfraHeader>
                  <InfraName>{node.name}</InfraName>
                  <Heartbeat $status={node.status} />
                </InfraHeader>
                <InfraMeta>
                  <div>
                    <span>Region</span>
                    <strong>{node.region}</strong>
                  </div>
                  <div>
                    <span>Latency</span>
                    <strong>{node.latency}</strong>
                  </div>
                  <div>
                    <span>Uptime</span>
                    <strong>{node.uptime}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{node.status}</strong>
                  </div>
                </InfraMeta>
              </InfraNode>
            ))}
          </InfraGrid>
        </Card>

        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardTitle>최근 운영 이벤트</CardTitle>
              <CardDescription>
                관리자가 바로 확인해야 할 주요 변경 사항입니다.
              </CardDescription>
            </CardTitleGroup>
          </CardHeader>
          <EventList>
            {recentEvents.map((event) => (
              <EventItem key={event}>{event}</EventItem>
            ))}
          </EventList>
        </Card>
      </DashboardGrid>
    </Page>
  );
};

export default DashboardPage;
